import React, { useMemo } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

/**
 * Pick an org's timezone from a list, so a name the backend cannot load can
 * never be sent.
 *
 * On 2026-09-29 the admin panel's free-text timezone field stored `London` on
 * the dooglie org, and every live-call card poll failed on the backend for two
 * hours (PYTHON-1Z4). The backend now refuses such a name with a 400; this
 * control keeps the page from sending one at all. It is an Autocomplete without
 * `freeSolo`: typing only filters the list, and text that matches nothing is
 * dropped on blur rather than becoming the value.
 *
 * The options are the browser's own IANA list plus `UTC`, which that list
 * omits. Every zone in Node 20's list (418) is one the backend accepts. `""` is
 * "Not set": the backend stores it as absent and shows times in UTC. A stored
 * value missing from this browser's list still shows as the selection, so
 * saving the form's other fields does not change it.
 */

const NOT_SET = "";

const labelOf = (tz: string) => (tz === NOT_SET ? "Not set (UTC)" : tz);

// Match "new york" to America/New_York as well as "new_york".
const filterOptions = createFilterOptions<string>({
  stringify: (tz) => `${labelOf(tz)} ${labelOf(tz).replace(/_/g, " ")}`,
});

type Props = {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
  "aria-label"?: string;
};

const TimezoneSelect: React.FC<Props> = ({ value, onChange, disabled, ...rest }) => {
  const options = useMemo(() => {
    const zones = new Set(Intl.supportedValuesOf("timeZone"));
    zones.add("UTC");
    if (value) zones.add(value);
    return [NOT_SET, ...Array.from(zones).sort()];
  }, [value]);

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={labelOf}
      filterOptions={filterOptions}
      disableClearable
      autoHighlight
      openOnFocus
      disabled={disabled}
      // The page's own input styling (.dg-field / .dg-ap-form-table) applies to
      // a plain <input>; MUI only supplies the list.
      renderInput={(params) => (
        <div ref={params.InputProps.ref}>
          {/* A picker over a fixed list, not free text: Autocomplete owns this input. */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <input
            type="text"
            {...params.inputProps}
            aria-label={rest["aria-label"]}
            placeholder="Search timezones…"
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
      )}
    />
  );
};

export default TimezoneSelect;
